import { useState, useMemo, useEffect } from 'react';
import {
  Modal,
  RadioButtonGroup,
  RadioButton,
  Search,
  DataTableSkeleton,
  InlineNotification,
} from '@carbon/react';
import {
  TypeCurveForecast,
  PdpDeclineForecast,
  AvailableForecastsResponse,
} from 'types/api-responses';
import type { VolumeForecastType } from 'components/volume-source-picker';
import styles from './edit-volume-source-modal.module.scss';
import { ForecastList } from './forecast-list';

export interface EditVolumeSourceModalViewProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Callback when save is clicked with the selected volume source */
  onSave: (
    volumeSourceId: string,
    volumeSourceType: string,
    volumeSourceHref?: string,
    forecastSource?: string,
    dcaScenario?: string
  ) => void;
  /** The slot ID being edited */
  slotId: string;
  /** Optional well name for display */
  wellName?: string;
  /** The current volume source ID (for determining if selection has changed) */
  currentVolumeSourceId?: string;
  /** Candidate forecasts; `null` until loaded. Supplied by the data wrapper. */
  data: AvailableForecastsResponse | null;
  /** Whether the candidate forecasts are loading. */
  isLoading: boolean;
  /** Load error message, or `null`. */
  error: string | null;
}

/**
 * Presentational Edit Volume Source modal: forecast type toggle, search, and
 * selectable forecast list, with the candidate data supplied as props. Data
 * fetching is owned by the caller (a Jotai wrapper on the legacy page, a React
 * Query hook in eval3-new), so this core stays decoupled from any data source.
 *
 * @param {EditVolumeSourceModalViewProps} props - Modal state, callbacks, and candidate data.
 * @return {JSX.Element} The modal element.
 */
export const EditVolumeSourceModalView = ({
  isOpen,
  onClose,
  onSave,
  slotId,
  wellName,
  currentVolumeSourceId,
  data,
  isLoading,
  error,
}: EditVolumeSourceModalViewProps) => {
  const [selectedType, setSelectedType] =
    useState<VolumeForecastType>('pdp_decline');
  const [selectedForecastId, setSelectedForecastId] = useState<string | null>(
    currentVolumeSourceId ?? null
  );
  const [searchQuery, setSearchQuery] = useState('');

  // Reset selection + search when the modal opens for a (new) slot.
  useEffect(() => {
    if (isOpen && slotId) {
      setSelectedForecastId(currentVolumeSourceId ?? null);
      setSearchQuery('');
    }
  }, [isOpen, slotId, currentVolumeSourceId]);

  // Filter forecasts based on selected type and search query
  const filteredForecasts = useMemo(() => {
    if (!data) return [];

    const forecasts: (TypeCurveForecast | PdpDeclineForecast)[] =
      selectedType === 'pdp_decline' ? data.pdpDeclines : data.typeCurves;

    if (!searchQuery) return forecasts;

    const lowerQuery = searchQuery.toLowerCase();
    return forecasts.filter((f) => f.name.toLowerCase().includes(lowerQuery));
  }, [data, selectedType, searchQuery]);

  const hasSelectionChanged = selectedForecastId !== currentVolumeSourceId;

  const handleSave = () => {
    if (selectedForecastId) {
      if (selectedType === 'type_curve') {
        const selectedForecast = data?.typeCurves.find(
          (tc) => String(tc.id) === selectedForecastId
        );
        const href = selectedForecast?.href ?? undefined;
        onSave(selectedForecastId, selectedType, href);
      } else {
        // PDP decline — extract forecastSource and dcaScenario for DCA support
        const selectedForecast = data?.pdpDeclines.find(
          (f) => String(f.id) === selectedForecastId
        );
        onSave(
          selectedForecastId,
          selectedType,
          undefined,
          selectedForecast?.forecastSource,
          selectedForecast?.dcaScenario
        );
      }
    }
  };

  /**
   * Handles type toggle change.
   * @param {string} value - The new forecast type
   */
  const handleTypeChange = (value: string | number | undefined) => {
    setSelectedType(value as VolumeForecastType);
  };

  const modalLabel = `Slot ID: ${slotId}${
    wellName ? ` | Well Name: ${wellName}` : ''
  }`;

  return (
    <Modal
      open={isOpen}
      onRequestClose={onClose}
      modalHeading="Edit Volume Source"
      modalLabel={modalLabel}
      primaryButtonText="Save"
      primaryButtonDisabled={!hasSelectionChanged || !selectedForecastId}
      onRequestSubmit={handleSave}
      secondaryButtonText="Cancel"
      size="lg"
      className={styles.modal}
    >
      <div className={styles.modalContent}>
        {error && (
          <InlineNotification
            kind="error"
            title={error}
            lowContrast
            hideCloseButton
          />
        )}

        <RadioButtonGroup
          legendText="Select Type"
          name={`forecast-type-${slotId}`}
          valueSelected={selectedType}
          onChange={handleTypeChange}
          className={styles.typeToggle}
        >
          <RadioButton
            labelText="PDP Decline"
            value="pdp_decline"
            id="pdp-decline-radio"
          />
          <RadioButton
            labelText="Type Curve Forecast"
            value="type_curve"
            id="type-curve-radio"
          />
        </RadioButtonGroup>

        <p className={styles.helperText}>
          Select a forecast to use as the volume source for this slot. The
          selected forecast will determine the associated decline data used in
          evaluation.
        </p>

        <div className={styles.searchListContainer}>
          <Search
            placeholder="Search forecasts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            labelText="Search"
            closeButtonLabelText="Clear search"
            size="lg"
            className={styles.searchInput}
          />

          {isLoading ? (
            <DataTableSkeleton
              showHeader={false}
              showToolbar={false}
              columnCount={3}
              rowCount={5}
            />
          ) : (
            <ForecastList
              forecasts={filteredForecasts}
              selectedId={selectedForecastId}
              onSelect={setSelectedForecastId}
              forecastType={selectedType}
            />
          )}

          <p className={styles.rowCount}>
            {filteredForecasts.length}{' '}
            {filteredForecasts.length === 1 ? 'Row' : 'Rows'}
          </p>
        </div>
      </div>
    </Modal>
  );
};
